import React, { useRef, useEffect } from 'react';
import { QUERY, MUTATION } from '../gql-operations';
// Note: formatS3Filename removed - server now generates UUID path
import { useMutation, useQuery } from '@apollo/client';
import { AuthContext } from '../util/context';
import { invoke } from '@tauri-apps/api/core';
import { uploadFileToS3 } from '../utils/s3Upload';
import useUpdateMessageMutation from './useUpdateMessageMutation';
import useUpdateCheckboxes from './useUpdateCheckboxes';
import useRedirectErrors from '../errors/useRedirectErrors';
import useAddMetadata from '../hooks/useAddMetadata';
import customAnalytics from '../util/custom-analytics';
import { isFileTypeImage } from '../constants/fileUtils';
import useMultipartUpload from './useMultipartUpload';
import useMultipartUploadTauri from './useMultipartUploadTauri';
import { isTauriEnvironment } from '../utils/tauri';
import { DEFAULT_SORT_FIELD } from '../constants/dateField';
import {
    attachAudioUploadMetadata,
    getAudioExtensionFromMimeType,
    getAudioUploadMetadata,
    normalizeAudioMimeType,
    resolveAudioDuration
} from '../util/audioUploadMetadata';

// Feature flag: Set to false to disable client-side metadata extraction and test server-side extraction
// Client-side extraction is preferred (faster, no server load) - server-side is fallback only
const ENABLE_CLIENT_SIDE_METADATA_EXTRACTION = true;

const hasNativePath = file => Boolean(
    file?.path &&
    typeof file.path === 'string' &&
    (file.path.startsWith('/') || /^[a-zA-Z]:[\\/]/.test(file.path))
);

function useS3Request() {
    // Ref to track latest uploadProgress state (avoids stale closures)
    const uploadProgressRef = useRef({});
    // Ref to track preview URLs for cleanup
    const previewUrlsRef = useRef({});
    const { updateCheckboxes } = useUpdateCheckboxes();
    const [createMessage] = useMutation(MUTATION.CREATE_MESSAGE, {
        onCompleted: res => {
            customAnalytics.message_sent(res.createMessage);
        },
        onError: error => {
            passingErrors(error);
            setClientError(error.graphQLErrors);
        }
    });
    const [createMessageFromAudioMutation] = useMutation(MUTATION.CREATE_MESSAGE_FROM_AUDIO);
    const { updateFiles } = useUpdateMessageMutation();
    const [signS3] = useMutation(MUTATION.S3_SIGN);
    const { data } = useQuery(QUERY.ME);
    const { passingErrors } = useRedirectErrors();
    const { savedFromMeta } = useAddMetadata();
    const { uploadMultipart, shouldUseMultipart } = useMultipartUpload();
    const { uploadMultipartTauri } = useMultipartUploadTauri();

    const {
        state: { uploadProgress },
        context: {
            setUploadProgress,
            setClientError,
            addUploadingMessage,
            updateUploadingMessage,
            removeUploadingMessage,
            registerUploadCancelHandler
        }
    } = React.useContext(AuthContext);

    // Keep uploadProgress ref in sync with state
    useEffect(() => {
        uploadProgressRef.current = uploadProgress || {};
    }, [uploadProgress]);

    // Generate a temporary ID for placeholder messages
    const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const fileFormat = name => name.split('.').pop();

    const normalizeFileEntry = entry => {
        if (entry && (entry.file instanceof Blob || entry.file instanceof File)) {
            return {
                file: entry.file,
                metadata: entry.metadata || getAudioUploadMetadata(entry.file)
            };
        }

        if (entry && (entry.originalFile instanceof Blob || entry.originalFile instanceof File)) {
            return {
                file: entry.originalFile,
                metadata: entry.metadata || getAudioUploadMetadata(entry.originalFile)
            };
        }

        if (entry instanceof Blob || entry instanceof File) {
            return {
                file: entry,
                metadata: getAudioUploadMetadata(entry)
            };
        }

        return {
            file: entry,
            metadata: null
        };
    };

    const normalizeAudioFileMetadata = (file, metadata) => {
        const attachedMetadata = metadata || getAudioUploadMetadata(file);
        const normalizedMime = normalizeAudioMimeType(attachedMetadata?.mimeType || file.type) || file.type;
        const format = getAudioExtensionFromMimeType(normalizedMime);

        return {
            mime: normalizedMime,
            format,
            metadata: attachedMetadata
        };
    };

    const resolveUploadDisplayName = (file, metadata) => {
        const metadataDisplayName = String(metadata?.displayName || '').trim();
        if (metadataDisplayName) {
            return metadataDisplayName;
        }

        return String(file?.name || '').trim() || 'file';
    };

    const makeS3RequestFile = (signedData, file, message, onProgress, options = {}) => {
        const isTauri = isTauriEnvironment();
        const { signal, progressKey: requestedProgressKey, uploadId, onNativeUploadCompleted } = options;
        let cancelPromise;

        const uploadPromise = new Promise(async (resolve, reject) => {
            const progressKey = requestedProgressKey || (message ? `${message.id}_${isFileTypeImage(file.type) ? 'image' : 'file'}` : file.name);
            const fileType = isFileTypeImage(file.type) ? 'image' : 'file';

            // Helper to update progress using ref (avoids stale closure)
            const updateProgress = (state, percentage) => {
                if (signal?.aborted) return;
                const currentProgress = uploadProgressRef.current || {};
                const newProgress = {
                    ...currentProgress,
                    [progressKey]: { state, percentage, type: fileType, size: file.size }
                };
                uploadProgressRef.current = newProgress;
                setUploadProgress(newProgress);

                // Call the progress callback if provided
                if (onProgress) {
                    onProgress(percentage);
                }
            };

            const clearUploadProgress = () => {
                const currentProgress = uploadProgressRef.current || {};
                const rest = {};
                Object.entries(currentProgress).forEach(([k, v]) => {
                    if (k !== progressKey) {
                        rest[k] = v;
                    }
                });
                uploadProgressRef.current = rest;
                setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            };

            try {
                if (signal?.aborted) {
                    clearUploadProgress();
                    reject(new Error('Upload cancelled'));
                    return;
                }

                if (isTauri && hasNativePath(file)) {
                    // Tauri native upload flow
                    // Use native file path to avoid reading the file into JS memory.
                    const nativePath = file.path;

                    // Update progress
                    updateProgress('pending', 50);

                    // Use Tauri upload function
                    await uploadFileToS3(nativePath, signedData.signedRequest, file.type, null, { signal, uploadId });
                    onNativeUploadCompleted?.();

                    // Update progress to completion
                    updateProgress('done', 100);

                    // Clear progress after successful upload
                    clearUploadProgress();

                    resolve(signedData.url);
                } else {
                    // Browser upload flow (also handles Tauri when file is in webview memory without native path)
                    const xhr = new XMLHttpRequest();

                    // Set up cancellation
                    cancelPromise = () => {
                        try {
                            xhr.abort();
                        } catch (_) {}
                        clearUploadProgress();
                        reject(new Error('Upload cancelled'));
                    };

                    if (signal) {
                        signal.addEventListener('abort', () => {
                            cancelPromise?.();
                        }, { once: true });
                    }

                    xhr.upload.addEventListener('progress', event => {
                        if (signal?.aborted) return;
                        if (event.lengthComputable) {
                            const progressPercentage = (event.loaded / event.total) * 100;
                            updateProgress('pending', progressPercentage);
                        }
                    });

                    xhr.addEventListener('load', () => {
                        updateProgress('done', 100);
                        clearUploadProgress();
                        resolve(signedData.url);
                    });

                    xhr.addEventListener('error', () => {
                        updateProgress('error', 0);
                        reject(xhr.response);
                    });

                    xhr.addEventListener('abort', () => {
                        clearUploadProgress();
                        reject(new Error('Upload cancelled'));
                    });

                    xhr.open('PUT', signedData.signedRequest);
                    xhr.setRequestHeader('Content-Type', file.type);
                    xhr.send(file);
                }
            } catch (error) {
                if (signal?.aborted || error?.message === 'Upload cancelled' || error?.message === 'Upload aborted') {
                    clearUploadProgress();
                } else {
                    console.error('Upload failed:', error);
                    updateProgress('error', 0);
                }
                reject(error);
            }
        });

        return {
            promise: uploadPromise,
            cancel: () => cancelPromise && cancelPromise()
        };
    };

    const getImageDimensions = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            const img = new Image();

            reader.onload = () => {
                img.src = reader.result;
                img.onload = () => {
                    resolve({
                        width: img.width,
                        height: img.height,
                        aspectRatio: img.height / img.width
                    });
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);

            reader.readAsDataURL(file);
        });
    };

    const getVideoDimensions = (file) => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            const objectUrl = URL.createObjectURL(file);

            const cleanup = () => {
                video.onloadedmetadata = null;
                video.onerror = null;
                video.removeAttribute('src');
                video.load();
                URL.revokeObjectURL(objectUrl);
            };

            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                const dimensions = {
                    width: video.videoWidth,
                    height: video.videoHeight,
                    aspectRatio: video.videoHeight / video.videoWidth,
                    duration: video.duration
                };
                console.log('[useS3Request] Video dimensions extracted:', {
                    fileName: file.name,
                    fileSize: file.size,
                    width: dimensions.width,
                    height: dimensions.height,
                    aspectRatio: dimensions.aspectRatio,
                    duration: dimensions.duration,
                    durationFormatted: `${Math.floor(dimensions.duration / 60)}:${String(Math.floor(dimensions.duration % 60)).padStart(2, '0')}`
                });
                cleanup();
                resolve(dimensions);
            };
            video.onerror = (error) => {
                cleanup();
                reject(error);
            };

            video.src = objectUrl;
        });
    };

    const getAudioDuration = (file) => {
        return new Promise((resolve, reject) => {
            const audio = document.createElement('audio');
            const objectUrl = URL.createObjectURL(file);

            const cleanup = () => {
                audio.onloadedmetadata = null;
                audio.onerror = null;
                audio.removeAttribute('src');
                audio.load();
                URL.revokeObjectURL(objectUrl);
            };

            audio.preload = 'metadata';
            audio.onloadedmetadata = () => {
                const durationData = {
                    duration: audio.duration
                };
                console.log('[useS3Request] Audio duration extracted:', {
                    fileName: file.name,
                    fileSize: file.size,
                    duration: durationData.duration,
                    durationFormatted: `${Math.floor(durationData.duration / 60)}:${String(Math.floor(durationData.duration % 60)).padStart(2, '0')}`
                });
                cleanup();
                resolve(durationData);
            };
            audio.onerror = (error) => {
                cleanup();
                reject(error);
            };

            audio.src = objectUrl;
        });
    };


    const sendFileMutation = async (file, values, folder, autoCheckbox, options = {}) => {
        const isTauri = isTauriEnvironment();
        const tempId = generateTempId();
        let abortController = null;
        let nativeUploadCompleted = false;

        try {
            if (!(file instanceof Blob || file instanceof File)) {
                throw new Error('Invalid file object provided');
            }

            const rawAudioMetadata = getAudioUploadMetadata(file);
            if (rawAudioMetadata) {
                attachAudioUploadMetadata(file, rawAudioMetadata);
            }

            const isAudioFile = file.type.includes('audio') || rawAudioMetadata?.mimeType?.startsWith('audio/');
            const audioFileMetadata = isAudioFile ? normalizeAudioFileMetadata(file, rawAudioMetadata) : null;
            const resolvedMime = audioFileMetadata?.mime || file.type;
            const typeFormat = resolvedMime.split('/');
            const resolvedFormat = isAudioFile ? audioFileMetadata.format : fileFormat(file.name);
            const displayName = resolveUploadDisplayName(file, rawAudioMetadata);

            // Extract metadata first so placeholders and the createMessage payload
            // can carry dimensions/duration immediately when the WebView can read them.
            let imageDimensions = null;
            let videoDimensions = null;
            let audioDuration = null;
            
            if (ENABLE_CLIENT_SIDE_METADATA_EXTRACTION) {
                // For images: limit to 50MB to avoid memory issues
                // For videos: extract dimensions client-side for all sizes (lightweight operation, avoids server load)
                // For audio: extract duration client-side for all sizes (lightweight operation)
                const MAX_IMAGE_SIZE_FOR_DIMENSIONS = 50 * 1024 * 1024; // 50MB
                if (isFileTypeImage(file.type) && file.size < MAX_IMAGE_SIZE_FOR_DIMENSIONS) {
                    try {
                        imageDimensions = await getImageDimensions(file);
                    } catch (error) {
                        console.warn('[useS3Request] Failed to extract image dimensions:', error);
                        // Continue without dimensions
                    }
                } else if (file.type.includes('video')) {
                    // Video dimension extraction is lightweight client-side (just reads metadata)
                    // No size limit needed - this avoids heavy server-side processing
                    try {
                        videoDimensions = await getVideoDimensions(file);
                        console.log('[useS3Request] Video dimensions extracted successfully:', {
                            fileName: file.name,
                            dimensions: videoDimensions
                        });
                    } catch (error) {
                        console.warn('[useS3Request] Failed to extract video dimensions:', error);
                        if (isTauri) {
                            console.warn('[useS3Request] Tauri video upload will rely on server-side extraction');
                        }
                    }
                } else if (isAudioFile) {
                    // Audio duration extraction is lightweight client-side (just reads metadata)
                    // No size limit needed - this avoids heavy server-side processing
                    try {
                        const duration = await resolveAudioDuration({
                            providedDuration: audioFileMetadata?.metadata?.duration,
                            fileDuration: file.duration,
                            extractDuration: () => getAudioDuration(file).then(result => result?.duration)
                        });
                        audioDuration = duration !== null ? { duration } : null;
                        console.log('[useS3Request] Audio duration extracted successfully:', {
                            fileName: file.name,
                            duration: audioDuration
                        });
                    } catch (error) {
                        console.warn('[useS3Request] Failed to extract audio duration:', error);
                        if (isTauri) {
                            console.warn('[useS3Request] Tauri audio upload will rely on server-side extraction');
                        }
                    }
                }
            } else {
                console.log('[useS3Request] ⚠️ Client-side metadata extraction disabled - server fallback will be used');
                if (isAudioFile) {
                    const duration = await resolveAudioDuration({
                        providedDuration: audioFileMetadata?.metadata?.duration,
                        fileDuration: file.duration
                    });
                    audioDuration = duration !== null ? { duration } : null;
                }
            }

            const audioDurationValue = isAudioFile ? (audioDuration?.duration ?? null) : undefined;

            // Create placeholder message and add to context immediately
            // This makes the message appear in the list instantly
            const optimisticTimestamp = new Date().toISOString();
            const clientRequestId = tempId;
            const placeholderMessage = {
                id: tempId,
                __typename: 'Message',
                clientRequestId,
                parentMessageId: options?.parentMessageId || null,
                referencedMessageId: options?.referencedMessageId || null,
                threadRootMessageId: null,
                laneKind: options?.laneKind || 'PERSONAL',
                aiRole: options?.aiRole || null,
                title: (values && values.title) ? values.title : '',
                body: null,
                createdAt: optimisticTimestamp,
                updatedAt: optimisticTimestamp,
                deletedAt: null,
                isCheckbox: autoCheckbox ? optimisticTimestamp : null,
                isChecked: null,
                isStarred: null,
                folder: {
                    id: folder.id,
                    title: folder.title,
                    owner: data.me.id,
                    parent: folder.parent,
                    deletedAt: null,
                    __typename: 'Folder'
                },
                file: {
                    url: null, // No URL yet - still uploading
                    name: file.name,
                    displayName,
                    type: typeFormat[0],
                    format: resolvedFormat,
                    size: file.size,
                    mime: resolvedMime,
                    ...(imageDimensions && {
                        width: imageDimensions.width,
                        height: imageDimensions.height,
                        aspect_ratio: imageDimensions.aspectRatio
                    }),
                    ...(videoDimensions && {
                        width: videoDimensions.width,
                        height: videoDimensions.height,
                        aspect_ratio: videoDimensions.aspectRatio,
                        duration: videoDimensions.duration
                    }),
                    ...(isAudioFile ? { duration: audioDurationValue } : {}),
                    __typename: 'Media'
                },
                image: null,
                urlMeta: [], // Empty array to avoid null access errors
                _isUploading: true, // Flag to identify this as a placeholder
                _tempId: tempId
            };

            // Log duration in placeholder message
            if (videoDimensions?.duration !== undefined) {
                console.log('[useS3Request] Video duration included in placeholder message:', {
                    fileName: file.name,
                    duration: videoDimensions.duration,
                    durationFormatted: `${Math.floor(videoDimensions.duration / 60)}:${String(Math.floor(videoDimensions.duration % 60)).padStart(2, '0')}`
                });
            }
            if (isAudioFile) {
                console.log('[useS3Request] Audio duration included in placeholder message:', {
                    fileName: file.name,
                    duration: audioDurationValue
                });
            }

            // Add placeholder to context - appears in list immediately
            addUploadingMessage(tempId, placeholderMessage);
            if (typeof options?.onOptimisticUploadMessage === 'function') {
                options.onOptimisticUploadMessage(placeholderMessage);
            }

            // Create an AbortController for cancellation
            abortController = new AbortController();
            if (typeof registerUploadCancelHandler === 'function') {
                registerUploadCancelHandler(tempId, () => abortController.abort());
            }

            let s3url;
            let s3key;

            // Use multipart upload for large files (> 100MB)
            // Server will generate UUID-based path automatically
            if (shouldUseMultipart(file)) {
                if (isTauri && hasNativePath(file)) {
                    // Tauri multipart upload via Rust backend
                    const result = await uploadMultipartTauri(file, file.name, null, (progress) => {
                        // Update progress in context
                        updateUploadingMessage(tempId, { progress });
                    }, { signal: abortController.signal, progressKey: tempId, uploadId: tempId });
                    s3url = result.url;
                    s3key = result.key;  // Server-generated path (e.g., "stored-files/{uuid}.{ext}")
                } else {
                    // Browser multipart upload via XHR (also handles Tauri when file has no native path)
                    const result = await uploadMultipart(file, file.name, null, (progress) => {
                        // Update progress in context
                        updateUploadingMessage(tempId, { progress });
                    }, { signal: abortController.signal, progressKey: tempId });
                    s3url = result.url;
                    s3key = result.key;  // Server-generated path (e.g., "stored-files/{uuid}.{ext}")
                }
            } else {
                // Use standard single-part upload for smaller files
                // Server will generate UUID-based path (stored-files/{uuid}.{ext})
                const signResponse = await signS3({
                    variables: {
                        data: {
                            fileName: file.name,  // Original filename - server will generate UUID path
                            fileType: resolvedMime,
                            fileSize: file.size
                        }
                    }
                });

                if (!signResponse?.data?.signS3) {
                    throw new Error('Failed to get signed URL from S3');
                }

                // Get the generated fileName from server response
                const s3FileName = signResponse.data.signS3.fileName;

                const { promise: uploadPromise } = makeS3RequestFile(signResponse.data.signS3, file, null, (progress) => {
                    // Update progress in context
                    updateUploadingMessage(tempId, { progress });
                }, {
                    signal: abortController.signal,
                    progressKey: tempId,
                    uploadId: tempId,
                    onNativeUploadCompleted: () => { nativeUploadCompleted = true; }
                });
                s3url = await uploadPromise;
                s3key = s3FileName;  // Use server-generated path
            }

            if (abortController?.signal?.aborted && !nativeUploadCompleted) {
                const cancelError = new Error('Upload cancelled');
                cancelError.isCancelled = true;
                throw cancelError;
            }

            // Update status to processing
            updateUploadingMessage(tempId, { status: 'processing', progress: 100 });

            // Prepare file object for mutation
            const fileObject = {
                url: s3url,
                name: s3key,
                displayName,
                type: typeFormat[0],
                format: resolvedFormat,
                size: file.size,
                mime: resolvedMime,
                ...(imageDimensions && {
                    width: imageDimensions.width,
                    height: imageDimensions.height,
                    aspect_ratio: imageDimensions.aspectRatio
                }),
                ...(videoDimensions && {
                    width: videoDimensions.width,
                    height: videoDimensions.height,
                    aspect_ratio: videoDimensions.aspectRatio,
                    duration: videoDimensions.duration
                }),
                ...(isAudioFile ? { duration: audioDurationValue } : {})
            };

            // Log what we're sending to server
            console.log('[useS3Request] 📤 Sending file to server via createMessage mutation:', {
                fileName: file.name,
                fileSize: file.size,
                hasImageDimensions: !!imageDimensions,
                hasVideoDimensions: !!videoDimensions,
                hasAudioDuration: !!audioDuration,
                imageData: imageDimensions ? {
                    width: imageDimensions.width,
                    height: imageDimensions.height,
                    aspectRatio: imageDimensions.aspectRatio
                } : null,
                videoData: videoDimensions ? {
                    width: videoDimensions.width,
                    height: videoDimensions.height,
                    aspectRatio: videoDimensions.aspectRatio,
                    duration: videoDimensions.duration
                } : null,
                audioData: audioDuration ? {
                    duration: audioDuration.duration
                } : null,
                fileObject: { ...fileObject, url: '[REDACTED]' }
            });

            const createMessageResult = await createMessage({
                variables: {
                    data: {
                        folderId: folder.id,
                        clientRequestId,
                        ...(options?.parentMessageId ? { parentMessageId: options.parentMessageId } : {}),
                        ...(options?.referencedMessageId ? { referencedMessageId: options.referencedMessageId } : {}),
                        ...(options?.laneKind ? { laneKind: options.laneKind } : {}),
                        ...(options?.aiRole ? { aiRole: options.aiRole } : {}),
                        ...(options?.aiProvider ? { aiProvider: options.aiProvider } : {}),
                        ...(options?.aiModel ? { aiModel: options.aiModel } : {}),
                        content: {
                            title: (values && values.title) ? values.title : '',
                            isCheckbox: autoCheckbox,
                            file: fileObject,
                            metadata: { ...savedFromMeta(), ...(isAudioFile ? { audioOrigin: rawAudioMetadata?.audioOrigin || 'uploaded_file' } : {}) }
                        }
                    }
                },
                update: async (store, { data: { createMessage: serverMessage } }) => {
                    if (autoCheckbox) {
                        updateCheckboxes([serverMessage]);
                    }

                    // Update FILTER_MESSAGES cache for "All" view
                    // Use readQuery/writeQuery instead of store.modify for reliability
                    try {
                        const allViewVariables = {
                            data: {
                                text: '',
                                type: [],
                                folderId: '',
                                isTrash: false,
                                sortBy: DEFAULT_SORT_FIELD
                            }
                        };

                        const allViewData = store.readQuery({
                            query: QUERY.FILTER_MESSAGES,
                            variables: allViewVariables
                        });

                        if (allViewData?.filterMessages?.messages) {
                            const existingMessages = allViewData.filterMessages.messages || [];
                            const messageExists = existingMessages.some(msg => msg.id === serverMessage.id);

                            if (!messageExists) {
                                // Ensure the message has all required fields for the cache
                                const messageWithRequiredFields = {
                                    ...serverMessage,
                                    lastViewedAt: serverMessage.lastViewedAt || null,
                                    lastMovedAt: serverMessage.lastMovedAt || null
                                };

                                const updatedMessages = [messageWithRequiredFields, ...existingMessages];

                                store.writeQuery({
                                    query: QUERY.FILTER_MESSAGES,
                                    variables: allViewVariables,
                                    data: {
                                        filterMessages: {
                                            ...allViewData.filterMessages,
                                            messages: updatedMessages
                                        }
                                    }
                                });

                                if (process.env.NODE_ENV === 'development') {
                                    console.log('📥 File message added to All view cache via mutation update:', serverMessage.id);
                                }
                            }
                        }
                    } catch (e) {
                        // Cache might not exist yet, that's okay
                        if (process.env.NODE_ENV === 'development') {
                            console.log('All view cache update skipped for file:', e.message);
                        }
                    }

                    // Dispatch event for components
                    window.dispatchEvent(new CustomEvent('messageCreated', {
                        detail: { message: serverMessage, folderId: folder.id }
                    }));
                }
            });
            const createdMessage = createMessageResult?.data?.createMessage;
            if (createdMessage?.id) {
                window.dispatchEvent(new CustomEvent('optimisticMessageResolved', {
                    detail: {
                        tempId,
                        clientRequestId,
                        folderId: folder.id,
                        message: createdMessage
                    }
                }));
            }

            // Remove placeholder after real message is created
            removeUploadingMessage(tempId);

            // Clean up progress using ref
            const { [tempId]: removed, ...rest } = uploadProgressRef.current || {};
            uploadProgressRef.current = rest;
            setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            return createdMessage;
            
        } catch (error) {
            const isCancelled = !nativeUploadCompleted && (abortController?.signal?.aborted || error?.isCancelled || error?.message === 'Upload cancelled' || error?.message === 'Upload aborted');
            if (isCancelled) {
                window.dispatchEvent(new CustomEvent('optimisticMessageFailed', { detail: { tempId } }));
                removeUploadingMessage(tempId);
                const currentProgress = uploadProgressRef.current || {};
                const rest = {};
                Object.entries(currentProgress).forEach(([k, v]) => {
                    if (k !== tempId) {
                        rest[k] = v;
                    }
                });
                uploadProgressRef.current = rest;
                setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                const cancelError = new Error('Upload cancelled');
                cancelError.isCancelled = true;
                throw cancelError;
            }

            // Mark as error but keep showing for a moment
            updateUploadingMessage(tempId, { status: 'error', progress: 0 });
            window.dispatchEvent(new CustomEvent('optimisticMessageFailed', {
                detail: {
                    tempId,
                    error
                }
            }));
            // Remove after delay so user can see the error
            setTimeout(() => removeUploadingMessage(tempId), 3000);

            // Clean up progress using ref
            const { [tempId]: removed, ...rest } = uploadProgressRef.current || {};
            uploadProgressRef.current = rest;
            setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            throw error;
        }
    };

    const sendToS3Files = async (values, files, folder, autoCheckbox, options = {}) => {
        if (files) {
            const filesToProcess = (Array.isArray(files) ? files : [files]).map(normalizeFileEntry);
            const createdMessages = [];
            const q = data?.me?.quota;
            const maxFileSize = q?.maxFileSize != null ? Number(q.maxFileSize) : null;
            const maxStorage = q?.maxStorage != null ? Number(q.maxStorage) : 0;
            const totalStorage = q?.totalStorage != null ? Number(q.totalStorage) : 0;
            const remainingStorage = maxStorage > 0 ? Math.max(0, maxStorage - totalStorage) : null;

            for (const entry of filesToProcess) {
                const file = entry.file;
                if (!data) {
                    throw new Error('File size exceeds the maximum allowed limit');
                }
                if (!(file instanceof Blob || file instanceof File)) {
                    throw new Error('Invalid file object provided');
                }
                if (entry.metadata) {
                    attachAudioUploadMetadata(file, entry.metadata);
                }
                if (maxFileSize != null && maxFileSize > 0 && file.size > maxFileSize) {
                    throw new Error('File size exceeds the maximum allowed limit');
                }
                if (remainingStorage != null && file.size > remainingStorage) {
                    throw new Error("You've reached the maximum storage limit. Delete some documents to free up space, or upgrade your account.");
                }
                const createdMessage = await sendFileMutation(file, values, folder, autoCheckbox, options);
                if (createdMessage?.id) {
                    createdMessages.push(createdMessage);
                }
            }
            return createdMessages;
        }
        return [];
    };

    const updateMessageFiles = async (files, message) => {
        const isTauri = isTauriEnvironment();

        try {
            const firstEntry = normalizeFileEntry(Array.isArray(files) ? files[0] : files);
            const file = firstEntry.file;

            if (!(file instanceof Blob || file instanceof File)) {
                throw new Error('Invalid file object provided');
            }

            if (firstEntry.metadata) {
                attachAudioUploadMetadata(file, firstEntry.metadata);
            }

            const rawAudioMetadata = firstEntry.metadata || getAudioUploadMetadata(file);
            const isAudioFile = file.type.includes('audio') || rawAudioMetadata?.mimeType?.startsWith('audio/');
            const audioFileMetadata = isAudioFile ? normalizeAudioFileMetadata(file, rawAudioMetadata) : null;
            const resolvedMime = audioFileMetadata?.mime || file.type;
            const resolvedFormat = isAudioFile ? audioFileMetadata.format : fileFormat(file.name);
            const typeFormat = resolvedMime.split('/');
            const displayName = resolveUploadDisplayName(file, rawAudioMetadata);

            let imageDimensions = null;
            let videoDimensions = null;
            let audioDuration = null;
            
            if (ENABLE_CLIENT_SIDE_METADATA_EXTRACTION) {
                // For images: limit to 50MB to avoid memory issues
                // For videos: extract dimensions client-side for all sizes (lightweight operation, avoids server load)
                // For audio: extract duration client-side for all sizes (lightweight operation)
                const MAX_IMAGE_SIZE_FOR_DIMENSIONS = 50 * 1024 * 1024; // 50MB
                if (isFileTypeImage(file.type) && file.size < MAX_IMAGE_SIZE_FOR_DIMENSIONS) {
                    try {
                        imageDimensions = await getImageDimensions(file);
                    } catch (error) {
                        console.warn('[useS3Request] Failed to extract image dimensions (updateMessageFiles):', error);
                        // Continue without dimensions
                    }
                } else if (file.type.includes('video')) {
                    // Video dimension extraction is lightweight client-side (just reads metadata)
                    // No size limit needed - this avoids heavy server-side processing
                    try {
                        videoDimensions = await getVideoDimensions(file);
                        console.log('[useS3Request] Video dimensions extracted successfully (updateMessageFiles):', {
                            fileName: file.name,
                            dimensions: videoDimensions
                        });
                    } catch (error) {
                        console.warn('[useS3Request] Failed to extract video dimensions (updateMessageFiles):', error);
                        if (isTauri) {
                            console.warn('[useS3Request] Tauri video update will rely on server-side extraction');
                        }
                    }
                } else if (isAudioFile) {
                    // Audio duration extraction is lightweight client-side (just reads metadata)
                    // No size limit needed - this avoids heavy server-side processing
                    try {
                        const duration = await resolveAudioDuration({
                            providedDuration: audioFileMetadata?.metadata?.duration,
                            fileDuration: file.duration,
                            extractDuration: () => getAudioDuration(file).then(result => result?.duration)
                        });
                        audioDuration = duration !== null ? { duration } : null;
                        console.log('[useS3Request] Audio duration extracted successfully (updateMessageFiles):', {
                            fileName: file.name,
                            duration: audioDuration
                        });
                    } catch (error) {
                        console.warn('[useS3Request] Failed to extract audio duration (updateMessageFiles):', error);
                        if (isTauri) {
                            console.warn('[useS3Request] Tauri audio update will rely on server-side extraction');
                        }
                    }
                }
            } else {
                console.log('[useS3Request] ⚠️ Client-side metadata extraction disabled (updateMessageFiles) - server fallback will be used');
                if (isAudioFile) {
                    const duration = await resolveAudioDuration({
                        providedDuration: audioFileMetadata?.metadata?.duration,
                        fileDuration: file.duration
                    });
                    audioDuration = duration !== null ? { duration } : null;
                }
            }

            const audioDurationValue = isAudioFile ? (audioDuration?.duration ?? null) : undefined;

            let s3url;
            let s3key;

            // Use multipart upload for large files (> 100MB)
            // Server will generate UUID-based path automatically
            if (shouldUseMultipart(file)) {
                if (isTauri && hasNativePath(file)) {
                    // Tauri multipart upload via Rust backend
                    const result = await uploadMultipartTauri(file, file.name, message);
                    s3url = result.url;
                    s3key = result.key;  // Server-generated path (e.g., "stored-files/{uuid}.{ext}")
                } else {
                    // Browser multipart upload via XHR
                    const result = await uploadMultipart(file, file.name, message);
                    s3url = result.url;
                    s3key = result.key;  // Server-generated path (e.g., "stored-files/{uuid}.{ext}")
                }
            } else {
                // Use standard single-part upload for smaller files
                // Server will generate UUID-based path (stored-files/{uuid}.{ext})
                const signResponse = await signS3({
                    variables: {
                        data: {
                            fileName: file.name,  // Original filename - server will generate UUID path
                            fileType: resolvedMime,
                            fileSize: file.size
                        }
                    }
                });

                // Get the generated fileName from server response
                const s3FileName = signResponse.data.signS3.fileName;

                const { promise: uploadPromise } = makeS3RequestFile(signResponse.data.signS3, file, message);
                s3url = await uploadPromise;
                s3key = s3FileName;  // Use server-generated path
            }

            const content = {
                file: {
                    url: s3url,
                    name: s3key,
                    displayName,
                    type: typeFormat[0],
                    format: resolvedFormat,
                    size: file.size,
                    mime: resolvedMime,
                    ...(imageDimensions && {
                        width: imageDimensions.width,
                        height: imageDimensions.height,
                        aspect_ratio: imageDimensions.aspectRatio
                    }),
                    ...(videoDimensions && {
                        width: videoDimensions.width,
                        height: videoDimensions.height,
                        aspect_ratio: videoDimensions.aspectRatio,
                        duration: videoDimensions.duration
                    }),
                    ...(isAudioFile ? { duration: audioDurationValue } : {})
                }
            };

            // Log duration before sending to server in updateFiles
            if (videoDimensions?.duration !== undefined) {
                console.log('[useS3Request] Sending video duration to server in updateFiles mutation:', {
                    fileName: file.name,
                    messageId: message.id,
                    duration: videoDimensions.duration,
                    durationFormatted: `${Math.floor(videoDimensions.duration / 60)}:${String(Math.floor(videoDimensions.duration % 60)).padStart(2, '0')}`,
                    fileObject: { ...content.file, url: '[REDACTED]' } // Don't log full URL
                });
            }
            if (isAudioFile) {
                console.log('[useS3Request] Sending audio duration to server in updateFiles mutation:', {
                    fileName: file.name,
                    messageId: message.id,
                    duration: audioDurationValue,
                    fileObject: { ...content.file, url: '[REDACTED]' } // Don't log full URL
                });
            }

            await updateFiles(message, content);

        } catch (error) {
            throw error;
        }
    };

    const updateMessageImage = async (images, message) => {
        const isTauri = isTauriEnvironment();

        try {
            const firstEntry = normalizeFileEntry(Array.isArray(images) ? images[0] : images);
            const file = firstEntry.file;

            if (!(file instanceof Blob || file instanceof File)) {
                throw new Error('Invalid file object provided');
            }

            let imageDimensions = null;

            if (ENABLE_CLIENT_SIDE_METADATA_EXTRACTION) {
                const MAX_IMAGE_SIZE_FOR_DIMENSIONS = 50 * 1024 * 1024;
                if (isFileTypeImage(file.type) && file.size < MAX_IMAGE_SIZE_FOR_DIMENSIONS) {
                    try {
                        imageDimensions = await getImageDimensions(file);
                    } catch (error) {
                        console.warn('[useS3Request] Failed to extract image dimensions (updateMessageImage):', error);
                    }
                }
            }

            let s3url;
            let s3key;

            if (shouldUseMultipart(file)) {
                if (isTauri && hasNativePath(file)) {
                    const result = await uploadMultipartTauri(file, file.name, message);
                    s3url = result.url;
                    s3key = result.key;
                } else {
                    const result = await uploadMultipart(file, file.name, message);
                    s3url = result.url;
                    s3key = result.key;
                }
            } else {
                const signResponse = await signS3({
                    variables: {
                        data: {
                            fileName: file.name,
                            fileType: file.type,
                            fileSize: file.size
                        }
                    }
                });

                if (!signResponse?.data?.signS3) {
                    throw new Error('Failed to get signed URL from S3');
                }

                const s3FileName = signResponse.data.signS3.fileName;
                const { promise: uploadPromise } = makeS3RequestFile(signResponse.data.signS3, file, message);
                s3url = await uploadPromise;
                s3key = s3FileName;
            }

            const content = {
                image: {
                    name: s3key,
                    url: s3url,
                    displayName: file.name,
                    type: file.type.split('/')[0],
                    format: fileFormat(file.name),
                    size: file.size,
                    ...(imageDimensions && {
                        width: imageDimensions.width,
                        height: imageDimensions.height,
                        aspect_ratio: imageDimensions.aspectRatio
                    })
                }
            };

            await updateFiles(message, content);
        } catch (error) {
            throw error;
        }
    };

    const sendImageToS3 = async (file, values, folder, autoCheckbox, options = {}) => {
        const isTauri = isTauriEnvironment();
        const tempId = generateTempId();
        let abortController = null;
        let nativeUploadCompleted = false;
        const resolvedFile = (file instanceof Blob || file instanceof File)
            ? file
            : (file?.originalFile instanceof Blob || file?.originalFile instanceof File)
                ? file.originalFile
                : (file?.file instanceof Blob || file?.file instanceof File)
                    ? file.file
                    : file;
        const fileName = file?.name || resolvedFile?.name || 'image';
        const fileType = resolvedFile?.type || file?.type || 'image/jpeg';
        const fileSize = resolvedFile?.size != null ? resolvedFile.size : file?.size;
        const typeFormat = String(fileType).split('/');

        try {
            if (!(resolvedFile instanceof Blob || resolvedFile instanceof File)) {
                throw new Error('Invalid file object provided');
            }

            // Get image dimensions FIRST (before creating placeholder) so we can include them
            let imageDimensions = null;
            
            // Prefer client-side extraction for images when possible so placeholders
            // and the final mutation both carry width/height immediately.
            if (ENABLE_CLIENT_SIDE_METADATA_EXTRACTION) {
                const MAX_SIZE_FOR_DIMENSIONS = 50 * 1024 * 1024; // 50MB
                if (fileSize < MAX_SIZE_FOR_DIMENSIONS) {
                    try {
                        imageDimensions = await getImageDimensions(resolvedFile);
                    } catch (error) {
                        console.warn('[useS3Request] Failed to extract image dimensions (sendImageToS3):', error);
                        if (isTauri) {
                            console.warn('[useS3Request] Tauri image upload will rely on server-side dimension extraction');
                        }
                    }
                }
            } else {
                console.log('[useS3Request] ⚠️ Client-side image dimension extraction disabled - server fallback will be used');
            }

            // Create preview URL from local file for immediate display
            const previewUrl = URL.createObjectURL(resolvedFile);
            // Store preview URL for cleanup
            previewUrlsRef.current[tempId] = previewUrl;

            // Create placeholder message with dimensions for proper sizing during upload
            const optimisticTimestamp = new Date().toISOString();
            const clientRequestId = tempId;
            const placeholderMessage = {
                id: tempId,
                __typename: 'Message',
                clientRequestId,
                parentMessageId: options?.parentMessageId || null,
                referencedMessageId: options?.referencedMessageId || null,
                threadRootMessageId: null,
                laneKind: options?.laneKind || 'PERSONAL',
                aiRole: options?.aiRole || null,
                title: (values && values.title) ? values.title : '',
                body: null,
                createdAt: optimisticTimestamp,
                updatedAt: optimisticTimestamp,
                deletedAt: null,
                isCheckbox: autoCheckbox ? optimisticTimestamp : null,
                isChecked: null,
                isStarred: null,
                folder: {
                    id: folder.id,
                    title: folder.title,
                    owner: data.me.id,
                    parent: folder.parent,
                    deletedAt: null,
                    __typename: 'Folder'
                },
                image: {
                    url: previewUrl, // Use local preview URL for immediate display
                    public_id: null, // Will be set after server generates path
                    name: fileName,
                    displayName: fileName,
                    type: typeFormat[0],
                    format: fileFormat(fileName),
                    size: fileSize,
                    // Include dimensions for proper sizing during upload preview
                    ...(imageDimensions && {
                        width: imageDimensions.width,
                        height: imageDimensions.height,
                        aspect_ratio: imageDimensions.aspectRatio
                    }),
                    __typename: 'Media',
                    _isPreview: true // Flag to indicate this is a preview URL
                },
                file: null,
                urlMeta: [],
                _isUploading: true,
                _tempId: tempId,
                _previewUrl: previewUrl // Store for cleanup later
            };

            addUploadingMessage(tempId, placeholderMessage);
            if (typeof options?.onOptimisticUploadMessage === 'function') {
                options.onOptimisticUploadMessage(placeholderMessage);
            }

            // Create an AbortController for cancellation
            abortController = new AbortController();
            if (typeof registerUploadCancelHandler === 'function') {
                registerUploadCancelHandler(tempId, () => abortController.abort());
            }

            let s3url;
            let s3key;

            // Use multipart upload for large files (> 100MB)
            // Server will generate UUID-based path automatically
            if (shouldUseMultipart(resolvedFile)) {
                if (isTauri && hasNativePath(resolvedFile)) {
                    // Tauri multipart upload via Rust backend
                    const result = await uploadMultipartTauri(resolvedFile, fileName, null, (progress) => {
                        updateUploadingMessage(tempId, { progress });
                    }, { signal: abortController.signal, progressKey: tempId, uploadId: tempId });
                    s3url = result.url;
                    s3key = result.key;  // Server-generated path (e.g., "stored-files/{uuid}.{ext}")
                } else {
                    // Browser multipart upload via XHR
                    const result = await uploadMultipart(resolvedFile, fileName, null, (progress) => {
                        updateUploadingMessage(tempId, { progress });
                    }, { signal: abortController.signal, progressKey: tempId });
                    s3url = result.url;
                    s3key = result.key;  // Server-generated path (e.g., "stored-files/{uuid}.{ext}")
                }
            } else {
                // Standard single-part upload
                // Server will generate UUID-based path (stored-files/{uuid}.{ext})
                const signResponse = await signS3({
                    variables: {
                        data: {
                            fileName: fileName,  // Original filename - server will generate UUID path
                            fileType: fileType,
                            fileSize: fileSize
                        }
                    }
                });

                if (!signResponse?.data?.signS3) {
                    throw new Error('Failed to get signed URL from S3');
                }

                // Get the generated fileName from server response
                const s3FileName = signResponse.data.signS3.fileName;

                const { promise: uploadPromise } = makeS3RequestFile(signResponse.data.signS3, resolvedFile, null, (progress) => {
                    updateUploadingMessage(tempId, { progress });
                }, {
                    signal: abortController.signal,
                    progressKey: tempId,
                    uploadId: tempId,
                    onNativeUploadCompleted: () => { nativeUploadCompleted = true; }
                });
                s3url = await uploadPromise;
                s3key = s3FileName;  // Use server-generated path
            }

            updateUploadingMessage(tempId, { status: 'processing', progress: 100 });

            const createMessageResult = await createMessage({
                variables: {
                    data: {
                        folderId: folder.id,
                        clientRequestId,
                        ...(options?.parentMessageId ? { parentMessageId: options.parentMessageId } : {}),
                        ...(options?.referencedMessageId ? { referencedMessageId: options.referencedMessageId } : {}),
                        ...(options?.laneKind ? { laneKind: options.laneKind } : {}),
                        ...(options?.aiRole ? { aiRole: options.aiRole } : {}),
                        ...(options?.aiProvider ? { aiProvider: options.aiProvider } : {}),
                        ...(options?.aiModel ? { aiModel: options.aiModel } : {}),
                        content: {
                            title: (values && values.title) ? values.title : '',
                            isCheckbox: autoCheckbox,
                            image: {
                                // S3 path stored in 'name' field (not 'public_id' which is Cloudinary-specific)
                                name: s3key, // S3 path (e.g., "stored-files/{uuid}.{ext}")
                                url: s3url, // S3 signed URL (server will convert to Bunny)
                                displayName: fileName,
                                type: typeFormat[0],
                                format: fileFormat(fileName),
                                size: fileSize,
                                ...(imageDimensions && {
                                    width: imageDimensions.width,
                                    height: imageDimensions.height,
                                    aspect_ratio: imageDimensions.aspectRatio
                                })
                            },
                            metadata: savedFromMeta()
                        }
                    }
                },
                update: async (store, { data: { createMessage: serverMessage } }) => {
                    if (autoCheckbox) {
                        updateCheckboxes([serverMessage]);
                    }

                    // Update FILTER_MESSAGES cache for "All" view
                    // Use readQuery/writeQuery instead of store.modify for reliability
                    try {
                        const allViewVariables = {
                            data: {
                                text: '',
                                type: [],
                                folderId: '',
                                isTrash: false,
                                sortBy: DEFAULT_SORT_FIELD
                            }
                        };

                        const allViewData = store.readQuery({
                            query: QUERY.FILTER_MESSAGES,
                            variables: allViewVariables
                        });

                        if (allViewData?.filterMessages?.messages) {
                            const existingMessages = allViewData.filterMessages.messages || [];
                            const messageExists = existingMessages.some(msg => msg.id === serverMessage.id);

                            if (!messageExists) {
                                // Ensure the message has all required fields for the cache
                                const messageWithRequiredFields = {
                                    ...serverMessage,
                                    lastViewedAt: serverMessage.lastViewedAt || null,
                                    lastMovedAt: serverMessage.lastMovedAt || null
                                };

                                const updatedMessages = [messageWithRequiredFields, ...existingMessages];

                                store.writeQuery({
                                    query: QUERY.FILTER_MESSAGES,
                                    variables: allViewVariables,
                                    data: {
                                        filterMessages: {
                                            ...allViewData.filterMessages,
                                            messages: updatedMessages
                                        }
                                    }
                                });

                                if (process.env.NODE_ENV === 'development') {
                                    console.log('📥 File message added to All view cache via mutation update:', serverMessage.id);
                                }
                            }
                        }
                    } catch (e) {
                        // Cache might not exist yet, that's okay
                        if (process.env.NODE_ENV === 'development') {
                            console.log('All view cache update skipped for file:', e.message);
                        }
                    }

                    // Dispatch event for components
                    window.dispatchEvent(new CustomEvent('messageCreated', {
                        detail: { message: serverMessage, folderId: folder.id }
                    }));
                }
            });
            const createdMessage = createMessageResult?.data?.createMessage;
            if (createdMessage?.id) {
                window.dispatchEvent(new CustomEvent('optimisticMessageResolved', {
                    detail: {
                        tempId,
                        clientRequestId,
                        folderId: folder.id,
                        message: createdMessage
                    }
                }));
            }

            // Clean up preview URL before removing message
            // Access the uploading message from context state
            const uploadingMessages = uploadProgress || {};
            const uploadingEntry = Object.values(uploadingMessages).find(
                entry => entry?.message?._tempId === tempId || entry?.message?.id === tempId
            );
            if (uploadingEntry?.message?._previewUrl) {
                URL.revokeObjectURL(uploadingEntry.message._previewUrl);
            }
            
            removeUploadingMessage(tempId);

            const { [tempId]: removed, ...rest } = uploadProgressRef.current || {};
            uploadProgressRef.current = rest;
            setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            return createdMessage;
            
        } catch (error) {
            // Clean up preview URL on error or cancellation
            if (previewUrlsRef.current[tempId]) {
                URL.revokeObjectURL(previewUrlsRef.current[tempId]);
                delete previewUrlsRef.current[tempId];
            }

            const isCancelled = !nativeUploadCompleted && (abortController?.signal?.aborted || error?.isCancelled || error?.message === 'Upload cancelled' || error?.message === 'Upload aborted');
            if (isCancelled) {
                window.dispatchEvent(new CustomEvent('optimisticMessageFailed', { detail: { tempId } }));
                removeUploadingMessage(tempId);
                const currentProgress = uploadProgressRef.current || {};
                const rest = {};
                Object.entries(currentProgress).forEach(([k, v]) => {
                    if (k !== tempId) {
                        rest[k] = v;
                    }
                });
                uploadProgressRef.current = rest;
                setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                const cancelError = new Error('Upload cancelled');
                cancelError.isCancelled = true;
                throw cancelError;
            }
            
            updateUploadingMessage(tempId, { status: 'error', progress: 0 });
            window.dispatchEvent(new CustomEvent('optimisticMessageFailed', {
                detail: {
                    tempId,
                    error
                }
            }));
            setTimeout(() => {
                removeUploadingMessage(tempId);
            }, 3000);

            const { [tempId]: removed, ...rest } = uploadProgressRef.current || {};
            uploadProgressRef.current = rest;
            setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            throw error;
        }
    };

    const sendRecordedAudioToTextMessage = async (_values, fileEntry, folder, autoCheckbox, options = {}) => {
        const entry = normalizeFileEntry(fileEntry);
        const file = entry.file;

        if (!folder?.id) {
            throw new Error('Folder is required');
        }

        if (!(file instanceof Blob || file instanceof File)) {
            throw new Error('Invalid audio file object provided');
        }

        if (entry.metadata) {
            attachAudioUploadMetadata(file, entry.metadata);
        }

        const rawAudioMetadata = entry.metadata || getAudioUploadMetadata(file);
        const audioFileMetadata = normalizeAudioFileMetadata(file, rawAudioMetadata);
        const resolvedMime = audioFileMetadata?.mime || file.type || 'audio/mp4';
        const normalizedDuration = await resolveAudioDuration({
            providedDuration: rawAudioMetadata?.duration,
            fileDuration: rawAudioMetadata?.duration,
            extractDuration: null
        });
        const audioDurationMs = Number.isFinite(normalizedDuration)
            ? Math.max(0, Math.round(normalizedDuration * 1000))
            : null;

        const signResponse = await signS3({
            variables: {
                data: {
                    fileName: file.name,
                    fileType: resolvedMime,
                    fileSize: file.size,
                    folder: 'audio-transcription-temp'
                }
            }
        });

        if (!signResponse?.data?.signS3) {
            throw new Error('Failed to get signed URL from S3');
        }

        const s3FileName = signResponse.data.signS3.fileName;
        const { promise: uploadPromise } = makeS3RequestFile(signResponse.data.signS3, file, null);
        await uploadPromise;

        const clientRequestId = generateTempId();
        const mutationResult = await createMessageFromAudioMutation({
            variables: {
                data: {
                    folderId: folder.id,
                    audioUploadKey: s3FileName,
                    audioMimeType: resolvedMime,
                    ...(audioDurationMs != null ? { audioDurationMs } : {}),
                    clientRequestId,
                    ...(options?.parentMessageId ? { parentMessageId: options.parentMessageId } : {}),
                    ...(options?.referencedMessageId ? { referencedMessageId: options.referencedMessageId } : {}),
                    ...(options?.laneKind ? { laneKind: options.laneKind } : {}),
                    ...(options?.aiRole ? { aiRole: options.aiRole } : {}),
                    ...(options?.aiProvider ? { aiProvider: options.aiProvider } : {}),
                    ...(options?.aiModel ? { aiModel: options.aiModel } : {}),
                    ...(options?.transcriptionOutputFormat
                        ? { outputFormat: options.transcriptionOutputFormat }
                        : {}),
                    metadata: { ...savedFromMeta(), audioOrigin: rawAudioMetadata?.audioOrigin || 'uploaded_file' }
                }
            }
        });

        const createdResult = mutationResult?.data?.createMessageFromAudio;
        if (!createdResult) {
            throw new Error('Failed to create message from audio');
        }

        if (createdResult.__typename === 'CreateMessageFromAudioError') {
            throw new Error(createdResult.message || 'Failed to transcribe audio');
        }

        customAnalytics.message_sent(
            autoCheckbox ? { ...createdResult, taskFolderMessage: true } : createdResult
        );

        return createdResult;
    };

    const uploadProfilePicture = async (file) => {
        try {
            if (!(file instanceof Blob || file instanceof File)) {
                throw new Error('Invalid file object provided');
            }

            // Get signed URL from server with profile-picture folder
            const signResponse = await signS3({
                variables: {
                    data: {
                        fileName: file.name,
                        fileType: file.type,
                        fileSize: file.size,
                        folder: 'profile-picture'
                    }
                }
            });

            if (!signResponse?.data?.signS3) {
                throw new Error('Failed to get signed URL from S3');
            }

            const s3FileName = signResponse.data.signS3.fileName;

            // Upload to S3
            const { promise: uploadPromise } = makeS3RequestFile(signResponse.data.signS3, file, null);
            const s3url = await uploadPromise;

            // Return S3 path (not URL) to save in database
            return {
                s3Path: s3FileName,
                url: s3url
            };
        } catch (error) {
            console.error('[useS3Request] Error uploading profile picture:', error);
            throw error;
        }
    };

    const uploadBackgroundImage = async (file) => {
        try {
            if (!(file instanceof Blob || file instanceof File)) {
                throw new Error('Invalid file object provided');
            }

            // Get signed URL from server with background folder
            const signResponse = await signS3({
                variables: {
                    data: {
                        fileName: file.name,
                        fileType: file.type,
                        fileSize: file.size,
                        folder: 'background'
                    }
                }
            });

            if (!signResponse?.data?.signS3) {
                throw new Error('Failed to get signed URL from S3');
            }

            const s3FileName = signResponse.data.signS3.fileName;

            // Upload to S3
            const { promise: uploadPromise } = makeS3RequestFile(signResponse.data.signS3, file, null);
            const s3url = await uploadPromise;

            // Return S3 path (not URL) to save in database
            return {
                s3Path: s3FileName,
                url: s3url
            };
        } catch (error) {
            console.error('[useS3Request] Error uploading background image:', error);
            throw error;
        }
    };

    return {
        sendToS3Files,
        sendRecordedAudioToTextMessage,
        sendImageToS3,
        updateMessageImage,
        updateMessageFiles,
        uploadProfilePicture,
        uploadBackgroundImage
    };
}

export default useS3Request;

// Solve aborting
// https://codesandbox.io/s/elegant-lake-5jnh3?fontsize=14&hidenavigation=1&theme=dark&file=/src/index.js
// https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequestEventTarget/onabort
// https://support.cloudinary.com/hc/en-us/articles/202519972-How-to-abort-an-in-progress-upload-
